<template>
  <div
    ref="editorContainer"
    class="shape-editor"
    :class="'shape-' + shape"
    :style="{ width: editorSize + 'px', height: editorSize + 'px' }"
  >
    <canvas
      ref="canvas"
      :width="size"
      :height="size"
      class="shape-editor-canvas"
    />
    <input ref="input1" type="file" accept="image/*" class="hidden-input" @change="onFileChange(1, $event)" />
    <input ref="input2" type="file" accept="image/*" class="hidden-input" @change="onFileChange(2, $event)" />
    <input ref="input3" type="file" accept="image/*" class="hidden-input" @change="onFileChange(3, $event)" />

    <template v-if="showOnePart">
      <div
        class="full-overlay full-overlay--single"
        :class="{ 'image-loaded': image1Loaded, 'is-dragging': dragHalf === 1 }"
        @mousedown="onHalfMouseDown(1, $event)"
        @touchstart.prevent="onHalfTouchStart(1, $event)"
        @click="onHalfClick(1)"
      >
        <v-icon
          v-if="!image1Loaded"
          large
          class="upload-icon upload-icon--center"
        >{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
      </div>
    </template>
    <template v-else-if="showThreeParts">
      <div
        class="full-overlay"
        @mousedown="onOverlayMouseDown"
        @touchstart.prevent="onOverlayTouchStart"
        @click="onOverlayClick"
      >
        <v-icon
          v-if="!image1Loaded"
          class="upload-icon-slice"
          :style="uploadIconStyle(1)"
          large
          @click.stop="disableFileUpload ? $emit('part-clicked', 1) : openFile(1)"
        >{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        <v-icon
          v-if="!image2Loaded"
          class="upload-icon-slice"
          style="left: 50%; top: 50%; transform: translate(-50%, -50%);"
          large
          @click.stop="disableFileUpload ? $emit('part-clicked', 2) : openFile(2)"
        >{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        <v-icon
          v-if="!image3Loaded"
          class="upload-icon-slice"
          :style="uploadIconStyle(3)"
          large
          @click.stop="disableFileUpload ? $emit('part-clicked', 3) : openFile(3)"
        >{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
      </div>
    </template>
    <template v-else>
      <template v-if="isHorizontal">
        <div
          class="half-overlay half-overlay--top"
          :class="{ 'image-loaded': image1Loaded, 'is-dragging': dragHalf === 1 }"
          @mousedown="onHalfMouseDown(1, $event)"
          @touchstart.prevent="onHalfTouchStart(1, $event)"
          @click="onHalfClick(1)"
        >
          <v-icon v-if="!image1Loaded" large class="upload-icon">{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        </div>
        <div
          class="half-overlay half-overlay--bottom"
          :class="{ 'image-loaded': image2Loaded, 'is-dragging': dragHalf === 2 }"
          @mousedown="onHalfMouseDown(2, $event)"
          @touchstart.prevent="onHalfTouchStart(2, $event)"
          @click="onHalfClick(2)"
        >
          <v-icon v-if="!image2Loaded" large class="upload-icon">{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        </div>
      </template>
      <template v-else>
        <div
          class="half-overlay half-overlay--left"
          :class="{ 'image-loaded': image1Loaded, 'is-dragging': dragHalf === 1 }"
          @mousedown="onHalfMouseDown(1, $event)"
          @touchstart.prevent="onHalfTouchStart(1, $event)"
          @click="onHalfClick(1)"
        >
          <v-icon v-if="!image1Loaded" large class="upload-icon">{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        </div>
        <div
          class="half-overlay half-overlay--right"
          :class="{ 'image-loaded': image2Loaded, 'is-dragging': dragHalf === 2 }"
          @mousedown="onHalfMouseDown(2, $event)"
          @touchstart.prevent="onHalfTouchStart(2, $event)"
          @click="onHalfClick(2)"
        >
          <v-icon v-if="!image2Loaded" large class="upload-icon">{{ disableFileUpload ? 'mdi-image-multiple-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ShapeEditorCanvas',
  props: {
    shape: { type: String, required: true },
    size: { type: Number, required: true },
    editorSize: { type: Number, required: true },
    numParts: { type: Number, required: true },
    isHorizontal: { type: Boolean, default: false },
    image1Loaded: { type: Boolean, default: false },
    image2Loaded: { type: Boolean, default: false },
    image3Loaded: { type: Boolean, default: false },
    dragHalf: { type: Number, default: null },
    /** When true, clicking a part does not open file picker; use gallery on the left instead. */
    disableFileUpload: { type: Boolean, default: false },
  },
  computed: {
    showOnePart() {
      return this.numParts === 1
    },
    showThreeParts() {
      return this.numParts === 3
    },
  },
  methods: {
    openFile(which) {
      const ref = this.$refs['input' + which]
      if (ref) ref.click()
    },
    uploadIconStyle(which) {
      if (this.isHorizontal) {
        return which === 1 ? 'left: 50%; top: 16%; transform: translate(-50%, -50%);' : 'left: 50%; top: 84%; transform: translate(-50%, -50%);'
      }
      return which === 1 ? 'left: 16%; top: 50%; transform: translate(-50%, -50%);' : 'left: 84%; top: 50%; transform: translate(-50%, -50%);'
    },
    getSegmentFromEvent(e) {
      const el = this.$refs.editorContainer
      if (!el) return 0
      const rect = el.getBoundingClientRect()
      const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left
      const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top
      if (this.isHorizontal) {
        const h = rect.height
        if (y < h / 3) return 1
        if (y < (h * 2) / 3) return 2
        return 3
      }
      const w = rect.width
      if (x < w / 3) return 1
      if (x < (w * 2) / 3) return 2
      return 3
    },
    onOverlayClick(e) {
      if (!this.showThreeParts) return
      const seg = this.getSegmentFromEvent(e)
      if (seg >= 1 && seg <= 3) {
        if (this.disableFileUpload) {
          this.$emit('part-clicked', seg)
          return
        }
        if (seg === 1 && !this.image1Loaded) this.openFile(1)
        else if (seg === 2 && !this.image2Loaded) this.openFile(2)
        else if (seg === 3 && !this.image3Loaded) this.openFile(3)
      }
    },
    onOverlayMouseDown(e) {
      if (!this.showThreeParts) return
      const seg = this.getSegmentFromEvent(e)
      if (seg >= 1 && seg <= 3) this.$emit('drag-start', seg, e)
    },
    onOverlayTouchStart(e) {
      if (!this.showThreeParts) return
      const seg = this.getSegmentFromEvent(e)
      if (seg >= 1 && seg <= 3) this.$emit('drag-start', seg, e)
    },
    onHalfMouseDown(which, e) {
      this.$emit('drag-start', which, e)
    },
    onHalfTouchStart(which, e) {
      this.$emit('drag-start', which, e)
    },
    onHalfClick(which) {
      if (this.disableFileUpload) {
        this.$emit('part-clicked', which)
        return
      }
      this.$emit('upload-click', which)
    },
    onFileChange(which, e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => {
        const img = new Image()
        img.onload = () => this.$emit('file-changed', { which, image: img })
        img.src = ev.target.result
      }
      reader.readAsDataURL(file)
      e.target.value = ''
    },
  },
}
</script>

<style scoped>
.shape-editor {
  position: relative;
  flex-shrink: 0;
  margin: 0 20px;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  background: transparent;
}

.shape-editor-canvas {
  display: block;
  width: 100%;
  height: 100%;
  background: transparent;
  position: relative;
  z-index: 0;
  pointer-events: none;
}

.hidden-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.half-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.85);
  transition: background 0.2s;
  z-index: 1;
  margin: 0;
}

.half-overlay:hover {
  background: rgba(255, 255, 255, 0.95);
}

.half-overlay--left.image-loaded,
.half-overlay--right.image-loaded,
.half-overlay--top.image-loaded,
.half-overlay--bottom.image-loaded {
  background: transparent;
  pointer-events: auto;
}

.half-overlay--left.image-loaded:hover,
.half-overlay--right.image-loaded:hover,
.half-overlay--top.image-loaded:hover,
.half-overlay--bottom.image-loaded:hover {
  background: rgba(255, 255, 255, 0.15);
}

.half-overlay--left { left: 0; }
.half-overlay--right { right: 0; left: auto; }

.half-overlay--top {
  top: 0; left: 0; right: 0; bottom: auto;
  width: 100%; height: 50%;
}

.half-overlay--bottom {
  top: 50%; left: 0; right: 0; bottom: 0;
  width: 100%; height: 50%;
}

.upload-icon {
  color: rgba(0, 0, 0, 0.4);
  font-size: 48px !important;
}

.half-overlay.image-loaded { cursor: grab; }
.half-overlay.image-loaded.is-dragging { cursor: grabbing; }

.full-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  cursor: pointer;
  background: transparent;
  z-index: 1;
}

.full-overlay:hover {
  background: rgba(255, 255, 255, 0.08);
}

.full-overlay--single.image-loaded {
  background: transparent;
  cursor: grab;
}

.full-overlay--single.image-loaded:hover {
  background: rgba(255, 255, 255, 0.15);
}

.full-overlay--single.image-loaded.is-dragging {
  cursor: grabbing;
}

.upload-icon--center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.upload-icon-slice {
  position: absolute;
  color: rgba(0, 0, 0, 0.4);
  font-size: 48px !important;
  pointer-events: auto;
}
</style>
